// frontend/src/components/Portfolio/PredictiveRiskAlerts.tsx
// Predictive Risk Alerts with:
// - Weather alert mapping to 18 climate hazards
// - 7-day forecast risk for portfolio
// - Seasonal risk patterns
// - Early warning system

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Badge,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  NotificationsActive as AlertIcon,
  WbSunny as SunIcon,
  AcUnit as ColdIcon,
  Thunderstorm as StormIcon,
  Flood as FloodIcon,
  LocalFireDepartment as FireIcon,
  Air as WindIcon,
  Terrain as EarthquakeIcon,
  Waves as TsunamiIcon,
  Landscape as LandslideIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

// ============================================================================
// HAZARD DEFINITIONS - 18 Climate Hazards
// ============================================================================
interface HazardInfo {
  label: string;
  icon: React.ComponentType<any>;
  color: string;
  season: number[];
}

const CLIMATE_HAZARDS: Record<string, HazardInfo> = {
  avalanche: { label: 'Avalanche', icon: LandslideIcon, color: '#9E9E9E', season: [12, 1, 2, 3] },
  flood: { label: 'Coastal Flood', icon: FloodIcon, color: '#2196F3', season: [8, 9, 10, 11] },
  flood_r: { label: 'River Flood', icon: FloodIcon, color: '#03A9F4', season: [3, 4, 5, 6] },
  cold: { label: 'Cold Wave', icon: ColdIcon, color: '#00BCD4', season: [12, 1, 2] },
  drought: { label: 'Drought', icon: SunIcon, color: '#FFC107', season: [6, 7, 8, 9] },
  earthquake: { label: 'Earthquake', icon: EarthquakeIcon, color: '#795548', season: [] }, // Year-round
  hail: { label: 'Hail', icon: CloudIcon, color: '#607D8B', season: [4, 5, 6, 7] },
  heat: { label: 'Heat Wave', icon: SunIcon, color: '#FF9800', season: [6, 7, 8] },
  hurricane: { label: 'Hurricane', icon: StormIcon, color: '#9C27B0', season: [6, 7, 8, 9, 10, 11] },
  ice: { label: 'Ice Storm', icon: ColdIcon, color: '#4DD0E1', season: [12, 1, 2, 3] },
  landslide: { label: 'Landslide', icon: LandslideIcon, color: '#8D6E63', season: [1, 2, 3, 4, 11, 12] },
  lightning: { label: 'Lightning', icon: StormIcon, color: '#FFEB3B', season: [5, 6, 7, 8] },
  tornado: { label: 'Tornado', icon: StormIcon, color: '#F44336', season: [3, 4, 5, 6] },
  tsunami: { label: 'Tsunami', icon: TsunamiIcon, color: '#1565C0', season: [] }, // Year-round
  volcanic: { label: 'Volcanic Activity', icon: FireIcon, color: '#D84315', season: [] }, // Year-round
  wildfire: { label: 'Wildfire', icon: FireIcon, color: '#FF5722', season: [6, 7, 8, 9, 10] },
  wind: { label: 'Strong Wind', icon: WindIcon, color: '#78909C', season: [3, 4, 10, 11] },
  winter: { label: 'Winter Storm', icon: ColdIcon, color: '#90CAF9', season: [11, 12, 1, 2, 3] },
};

// ============================================================================
// NWS ALERT TO HAZARD MAPPING
// ============================================================================
const ALERT_TO_HAZARD_MAP: Record<string, string[]> = {
  // Flood-related
  'flood': ['flood', 'flood_r'],
  'flash flood': ['flood_r'],
  'coastal flood': ['flood'],
  'river flood': ['flood_r'],
  'flood watch': ['flood', 'flood_r'],
  'flood warning': ['flood', 'flood_r'],
  'flood advisory': ['flood', 'flood_r'],
  'high surf': ['flood'],
  'rip current': ['flood'],
  'storm surge': ['flood'],
  
  // Winter-related
  'winter storm': ['winter', 'cold', 'ice'],
  'winter weather': ['winter', 'cold'],
  'blizzard': ['winter', 'cold', 'wind'],
  'ice storm': ['ice', 'winter'],
  'freezing rain': ['ice', 'winter'],
  'freeze': ['cold'],
  'frost': ['cold'],
  'cold': ['cold'],
  'wind chill': ['cold', 'wind'],
  'extreme cold': ['cold'],
  'hard freeze': ['cold'],
  'snow': ['winter'],
  'lake effect snow': ['winter'],
  
  // Heat-related
  'heat': ['heat'],
  'excessive heat': ['heat'],
  'heat advisory': ['heat'],
  'extreme heat': ['heat'],
  
  // Wind-related
  'wind': ['wind'],
  'high wind': ['wind'],
  'wind advisory': ['wind'],
  'dust storm': ['wind', 'drought'],
  'dust': ['drought'],
  
  // Severe weather
  'severe thunderstorm': ['lightning', 'hail', 'wind', 'tornado'],
  'thunderstorm': ['lightning', 'hail'],
  'tornado': ['tornado', 'wind'],
  'tornado watch': ['tornado'],
  'tornado warning': ['tornado'],
  'funnel cloud': ['tornado'],
  
  // Tropical
  'hurricane': ['hurricane', 'flood', 'wind'],
  'tropical storm': ['hurricane', 'flood', 'wind'],
  'tropical depression': ['hurricane', 'flood'],
  'hurricane watch': ['hurricane'],
  'hurricane warning': ['hurricane'],
  'tropical cyclone': ['hurricane'],
  
  // Fire-related
  'fire': ['wildfire'],
  'fire weather': ['wildfire', 'drought'],
  'red flag': ['wildfire'],
  'extreme fire': ['wildfire'],
  
  // Other hazards
  'avalanche': ['avalanche'],
  'earthquake': ['earthquake'],
  'tsunami': ['tsunami'],
  'volcano': ['volcanic'],
  'volcanic ash': ['volcanic'],
  'landslide': ['landslide'],
  'debris flow': ['landslide'],
  'dense fog': ['wind'], // Limited visibility
  'air quality': ['wildfire', 'drought'],
  'drought': ['drought'],
};

// ============================================================================
// INTERFACES
// ============================================================================
interface PortfolioItem {
  postal_code?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  asset_value?: number | string;
  risk_score?: number;
  [key: string]: any;
}

interface WeatherAlert {
  id: string;
  event: string;
  severity: string;
  headline: string;
  description: string;
  instruction?: string;
  effective: string;
  expires: string;
  areaDesc: string;
  is_active: boolean;
}

interface HazardForecast {
  hazard: string;
  label: string;
  color: string;
  currentRisk: number;
  forecastRisk: number;
  trend: 'increasing' | 'stable' | 'decreasing';
  activeAlerts: number;
  affectedLocations: number;
  affectedValue: number;
  seasonalPeak: boolean;
  warnings: string[];
}

interface PredictiveRiskAlertsProps {
  portfolio: {
    items: PortfolioItem[];
    id?: string;
    name?: string;
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const normalizeAssetValue = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
};

const formatCurrency = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const mapAlertToHazards = (alertEvent: string): string[] => {
  const eventLower = alertEvent.toLowerCase();
  
  for (const [keyword, hazards] of Object.entries(ALERT_TO_HAZARD_MAP)) {
    if (eventLower.includes(keyword)) {
      return hazards;
    }
  }
  
  // Fallback: try to match individual words
  const words = eventLower.split(/\s+/);
  for (const word of words) {
    if (ALERT_TO_HAZARD_MAP[word]) {
      return ALERT_TO_HAZARD_MAP[word];
    }
  }
  
  return [];
};

const getSeverityScore = (severity: string): number => {
  switch (severity?.toLowerCase()) {
    case 'extreme': return 10;
    case 'severe': return 8;
    case 'moderate': return 5;
    case 'minor': return 3;
    default: return 1;
  }
};

const isInSeason = (hazard: string, month: number): boolean => {
  const hazardInfo = CLIMATE_HAZARDS[hazard];
  if (!hazardInfo || hazardInfo.season.length === 0) return true; // Year-round
  return hazardInfo.season.includes(month);
};

const getSeasonalRiskMultiplier = (hazard: string, month: number): number => {
  const hazardInfo = CLIMATE_HAZARDS[hazard];
  if (!hazardInfo || hazardInfo.season.length === 0) return 1.0;
  
  if (hazardInfo.season.includes(month)) {
    // Peak season
    const midSeason = hazardInfo.season[Math.floor(hazardInfo.season.length / 2)];
    const distanceFromPeak = Math.min(
      Math.abs(month - midSeason),
      12 - Math.abs(month - midSeason)
    );
    return 1.5 - (distanceFromPeak * 0.1);
  }
  
  // Off season
  return 0.5;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function PredictiveRiskAlerts({ portfolio }: PredictiveRiskAlertsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [alerts, setAlerts] = useState<WeatherAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedHazard, setExpandedHazard] = useState<string | null>(null);
  const [detailDialog, setDetailDialog] = useState<HazardForecast | null>(null);
  
  const items = portfolio?.items || [];
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const currentDate = new Date();
  
  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    if (!portfolio?.id && items.length === 0) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      if (portfolio?.id) {
        const response = await axios.get(`${API_URL}/alerts/portfolio/${portfolio.id}`);
        const allAlerts: WeatherAlert[] = [];
        (response.data.alerts || []).forEach((loc: any) => {
          if (loc.alerts) {
            allAlerts.push(...loc.alerts);
          }
        });
        setAlerts(allAlerts);
      } else {
        const postalCodes = items
          .map(item => item.postal_code || item.postalCode)
          .filter(Boolean)
          .slice(0, 50); // Limit for performance
        
        if (postalCodes.length > 0) {
          const response = await axios.post(`${API_URL}/alerts/check`, { postalCodes });
          const allAlerts: WeatherAlert[] = [];
          (response.data || []).forEach((loc: any) => {
            if (loc.alerts) {
              allAlerts.push(...loc.alerts);
            }
          });
          setAlerts(allAlerts);
        }
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to fetch weather alerts');
    } finally {
      setLoading(false);
    }
  }, [portfolio?.id, items]);
  
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);
  
  // ============================================================================
  // CALCULATE HAZARD FORECASTS
  // ============================================================================
  const hazardForecasts = useMemo((): HazardForecast[] => {
    const forecasts: HazardForecast[] = [];
    
    // Map alerts to hazards
    const alertsByHazard: Record<string, WeatherAlert[]> = {};
    alerts.forEach(alert => {
      const hazards = mapAlertToHazards(alert.event);
      hazards.forEach(hazard => {
        if (!alertsByHazard[hazard]) alertsByHazard[hazard] = [];
        alertsByHazard[hazard].push(alert);
      });
    });
    
    // Calculate metrics for each hazard
    Object.entries(CLIMATE_HAZARDS).forEach(([hazardKey, hazardInfo]) => {
      const hazardAlerts = alertsByHazard[hazardKey] || [];
      const activeAlerts = hazardAlerts.filter(a => a.is_active);
      
      // Calculate affected locations and value
      let affectedLocations = 0;
      let affectedValue = 0;
      let totalRisk = 0;
      let locationCount = 0;
      
      items.forEach(item => {
        const hazardScore = parseFloat(String(item[hazardKey] || 0));
        if (hazardScore > 3) {
          affectedLocations++;
          affectedValue += normalizeAssetValue(item.asset_value);
        }
        totalRisk += hazardScore;
        locationCount++;
      });
      
      const avgRisk = locationCount > 0 ? totalRisk / locationCount : 0;
      
      // Calculate current risk (base + alerts)
      const alertSeverityBoost = activeAlerts.reduce((sum, a) => sum + getSeverityScore(a.severity), 0);
      const currentRisk = Math.min(10, avgRisk + (alertSeverityBoost * 0.5));
      
      // Calculate forecast risk (seasonal adjustment)
      const seasonalMultiplier = getSeasonalRiskMultiplier(hazardKey, currentMonth);
      const nextMonthMultiplier = getSeasonalRiskMultiplier(hazardKey, (currentMonth % 12) + 1);
      const forecastRisk = Math.min(10, avgRisk * nextMonthMultiplier + (alertSeverityBoost * 0.3));
      
      // Determine trend
      let trend: 'increasing' | 'stable' | 'decreasing' = 'stable';
      if (nextMonthMultiplier > seasonalMultiplier + 0.1) trend = 'increasing';
      else if (nextMonthMultiplier < seasonalMultiplier - 0.1) trend = 'decreasing';
      
      // Generate warnings
      const warnings: string[] = [];
      if (activeAlerts.length > 0) {
        warnings.push(`${activeAlerts.length} active alert(s) in your portfolio area`);
      }
      if (isInSeason(hazardKey, currentMonth) && avgRisk > 5) {
        warnings.push(`Peak season for ${hazardInfo.label} - elevated risk`);
      }
      if (trend === 'increasing' && avgRisk > 3) {
        warnings.push(`Risk expected to increase next month`);
      }
      if (affectedValue > 1e9) {
        warnings.push(`Over ${formatCurrency(affectedValue)} in exposed value`);
      }
      
      forecasts.push({
        hazard: hazardKey,
        label: hazardInfo.label,
        color: hazardInfo.color,
        currentRisk,
        forecastRisk,
        trend,
        activeAlerts: activeAlerts.length,
        affectedLocations,
        affectedValue,
        seasonalPeak: isInSeason(hazardKey, currentMonth),
        warnings,
      });
    });
    
    // Sort by current risk and active alerts
    return forecasts.sort((a, b) => {
      if (b.activeAlerts !== a.activeAlerts) return b.activeAlerts - a.activeAlerts;
      return b.currentRisk - a.currentRisk;
    });
  }, [items, alerts, currentMonth]);
  
  // ============================================================================
  // SUMMARY METRICS
  // ============================================================================
  const summaryMetrics = useMemo(() => {
    const activeAlertCount = alerts.filter(a => a.is_active).length;
    const hazardsWithAlerts = hazardForecasts.filter(h => h.activeAlerts > 0).length;
    const highRiskHazards = hazardForecasts.filter(h => h.currentRisk > 6).length;
    const increasingRisks = hazardForecasts.filter(h => h.trend === 'increasing').length;
    const peakSeasonHazards = hazardForecasts.filter(h => h.seasonalPeak && h.currentRisk > 3).length;
    
    const totalExposedValue = hazardForecasts.reduce((sum, h) => {
      if (h.currentRisk > 5) return sum + h.affectedValue;
      return sum;
    }, 0);
    
    return {
      activeAlertCount,
      hazardsWithAlerts,
      highRiskHazards,
      increasingRisks,
      peakSeasonHazards,
      totalExposedValue,
    };
  }, [alerts, hazardForecasts]);
  
  // ============================================================================
  // 7-DAY FORECAST DATA
  // ============================================================================
  const weekForecast = useMemo(() => {
    const days: { date: Date; dayName: string; risks: Record<string, number> }[] = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      date.setDate(date.getDate() + i);
      const dayMonth = date.getMonth() + 1;
      
      const risks: Record<string, number> = {};
      hazardForecasts.forEach(h => {
        const seasonalMult = getSeasonalRiskMultiplier(h.hazard, dayMonth);
        // Alerts decay over time
        const alertDecay = Math.max(0, 1 - (i * 0.15));
        risks[h.hazard] = Math.min(10, h.currentRisk * seasonalMult * alertDecay + (h.forecastRisk * (i / 7)));
      });
      
      days.push({
        date,
        dayName: i === 0 ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' }),
        risks,
      });
    }
    
    return days;
  }, [currentDate, hazardForecasts]);
  
  // ============================================================================
  // SEASONAL CALENDAR
  // ============================================================================
  const seasonalCalendar = useMemo(() => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    return months.map((name, index) => {
      const monthNum = index + 1;
      const activeHazards = Object.entries(CLIMATE_HAZARDS)
        .filter(([key, info]) => info.season.includes(monthNum))
        .map(([key]) => key);
      
      const isCurrentMonth = monthNum === currentMonth;
      
      return {
        name,
        monthNum,
        isCurrentMonth,
        activeHazards,
        riskLevel: activeHazards.length > 5 ? 'high' : activeHazards.length > 2 ? 'medium' : 'low',
      };
    });
  }, [currentMonth]);
  
  // ============================================================================
  // RENDER
  // ============================================================================
  if (items.length === 0) {
    return (
      <Alert severity="info">
        No portfolio data available for predictive risk analysis.
      </Alert>
    );
  }
  
  const getRiskColor = (risk: number): string => {
    if (risk > 7) return '#EF4444';
    if (risk > 5) return '#F97316';
    if (risk > 3) return '#F59E0B';
    return '#10B981';
  };
  
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': return <TrendingUpIcon sx={{ color: '#EF4444', fontSize: 18 }} />;
      case 'decreasing': return <TrendingUpIcon sx={{ color: '#10B981', fontSize: 18, transform: 'rotate(180deg)' }} />;
      default: return <ScheduleIcon sx={{ color: '#6B7280', fontSize: 18 }} />;
    }
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: summaryMetrics.activeAlertCount > 0 ? '#FEF2F2' : '#F0FDF4' }}>
            <Typography variant="h4" fontWeight={700} color={summaryMetrics.activeAlertCount > 0 ? 'error.main' : 'success.main'}>
              {summaryMetrics.activeAlertCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">Active Alerts</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} color="warning.main">
              {summaryMetrics.highRiskHazards}
            </Typography>
            <Typography variant="caption" color="text.secondary">High Risk Hazards</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} color="error.main">
              {summaryMetrics.increasingRisks}
            </Typography>
            <Typography variant="caption" color="text.secondary">Increasing Risks</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700} color="info.main">
              {summaryMetrics.peakSeasonHazards}
            </Typography>
            <Typography variant="caption" color="text.secondary">Peak Season</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={8} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#FEF3C7' }}>
            <Typography variant="h5" fontWeight={700} color="warning.dark">
              {formatCurrency(summaryMetrics.totalExposedValue)}
            </Typography>
            <Typography variant="caption" color="text.secondary">Value at Elevated Risk</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Current Alerts" icon={<Badge badgeContent={summaryMetrics.activeAlertCount} color="error"><AlertIcon /></Badge>} iconPosition="start" />
          <Tab label="7-Day Forecast" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Seasonal Calendar" icon={<CalendarIcon />} iconPosition="start" />
          <Tab label="All Hazards" icon={<WarningIcon />} iconPosition="start" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Tab 0: Current Alerts */}
          {activeTab === 0 && (
            <Box>
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography color="text.secondary">Fetching weather alerts...</Typography>
                </Box>
              ) : hazardForecasts.filter(h => h.activeAlerts > 0).length === 0 ? (
                <Alert severity="success" icon={<AlertIcon />}>
                  No active weather alerts affecting your portfolio locations.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {hazardForecasts.filter(h => h.activeAlerts > 0).map((forecast) => {
                    const HazardIcon = CLIMATE_HAZARDS[forecast.hazard]?.icon || WarningIcon;
                    return (
                      <Grid item xs={12} md={6} key={forecast.hazard}>
                        <Card 
                          sx={{ 
                            border: `2px solid ${forecast.color}`,
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 4 }
                          }}
                          onClick={() => setDetailDialog(forecast)}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <HazardIcon sx={{ color: forecast.color, fontSize: 28 }} />
                              <Typography variant="h6" fontWeight={600}>{forecast.label}</Typography>
                              <Chip 
                                label={`${forecast.activeAlerts} Alert${forecast.activeAlerts > 1 ? 's' : ''}`}
                                size="small"
                                color="error"
                                sx={{ ml: 'auto' }}
                              />
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Current Risk</Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ color: getRiskColor(forecast.currentRisk) }}>
                                  {forecast.currentRisk.toFixed(1)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Affected</Typography>
                                <Typography variant="h5" fontWeight={700}>
                                  {forecast.affectedLocations}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Value at Risk</Typography>
                                <Typography variant="h6" fontWeight={600} color="error.main">
                                  {formatCurrency(forecast.affectedValue)}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {forecast.warnings.length > 0 && (
                              <Alert severity="warning" sx={{ py: 0.5 }}>
                                <Typography variant="body2">{forecast.warnings[0]}</Typography>
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}
          
          {/* Tab 1: 7-Day Forecast */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Projected risk levels for top hazards over the next 7 days
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Hazard</TableCell>
                      {weekForecast.map((day, i) => (
                        <TableCell key={i} align="center" sx={{ fontWeight: day.dayName === 'Today' ? 700 : 400 }}>
                          {day.dayName}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Trend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hazardForecasts.slice(0, 8).map((forecast) => (
                      <TableRow key={forecast.hazard} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: forecast.color }} />
                            <Typography variant="body2" fontWeight={500}>{forecast.label}</Typography>
                          </Box>
                        </TableCell>
                        {weekForecast.map((day, i) => {
                          const risk = day.risks[forecast.hazard] || 0;
                          return (
                            <TableCell key={i} align="center">
                              <Chip
                                label={risk.toFixed(1)}
                                size="small"
                                sx={{
                                  bgcolor: `${getRiskColor(risk)}20`,
                                  color: getRiskColor(risk),
                                  fontWeight: 600,
                                  minWidth: 45,
                                }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell align="center">
                          {getTrendIcon(forecast.trend)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          {/* Tab 2: Seasonal Calendar */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Seasonal risk patterns - Plan ahead for peak hazard seasons
              </Typography>
              
              <Grid container spacing={1}>
                {seasonalCalendar.map((month) => (
                  <Grid item xs={6} sm={4} md={2} lg={1} key={month.name}>
                    <Paper 
                      sx={{ 
                        p: 1.5, 
                        textAlign: 'center',
                        border: month.isCurrentMonth ? '2px solid #00D4FF' : '1px solid #E5E7EB',
                        bgcolor: month.isCurrentMonth ? '#F0F9FF' : 
                          month.riskLevel === 'high' ? '#FEF2F2' :
                          month.riskLevel === 'medium' ? '#FFFBEB' : '#F0FDF4'
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={month.isCurrentMonth ? 700 : 500}>
                        {month.name}
                      </Typography>
                      <Typography variant="h5" fontWeight={700} sx={{ 
                        color: month.riskLevel === 'high' ? '#EF4444' :
                          month.riskLevel === 'medium' ? '#F59E0B' : '#10B981'
                      }}>
                        {month.activeHazards.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        hazards
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Current Month Peak Hazards ({seasonalCalendar.find(m => m.isCurrentMonth)?.name})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {hazardForecasts.filter(h => h.seasonalPeak).map((forecast) => (
                  <Chip
                    key={forecast.hazard}
                    label={forecast.label}
                    size="small"
                    sx={{
                      bgcolor: `${forecast.color}20`,
                      color: forecast.color,
                      borderColor: forecast.color,
                      borderWidth: 1,
                      borderStyle: 'solid',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Tab 3: All Hazards */}
          {activeTab === 3 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Hazard</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Current Risk</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Forecast</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Trend</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Alerts</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Locations</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Value at Risk</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Season</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hazardForecasts.map((forecast) => {
                    const HazardIcon = CLIMATE_HAZARDS[forecast.hazard]?.icon || WarningIcon;
                    return (
                      <TableRow 
                        key={forecast.hazard} 
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setDetailDialog(forecast)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HazardIcon sx={{ color: forecast.color, fontSize: 20 }} />
                            <Typography variant="body2" fontWeight={500}>{forecast.label}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={forecast.currentRisk.toFixed(1)}
                            size="small"
                            sx={{
                              bgcolor: `${getRiskColor(forecast.currentRisk)}20`,
                              color: getRiskColor(forecast.currentRisk),
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: getRiskColor(forecast.forecastRisk) }}>
                            {forecast.forecastRisk.toFixed(1)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{getTrendIcon(forecast.trend)}</TableCell>
                        <TableCell align="center">
                          {forecast.activeAlerts > 0 ? (
                            <Chip label={forecast.activeAlerts} size="small" color="error" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">{forecast.affectedLocations}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {formatCurrency(forecast.affectedValue)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {forecast.seasonalPeak ? (
                            <Chip label="Peak" size="small" color="warning" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">Off</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="sm" fullWidth>
        {detailDialog && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {React.createElement(
                  CLIMATE_HAZARDS[detailDialog.hazard]?.icon || WarningIcon,
                  { sx: { color: detailDialog.color, fontSize: 28 } }
                )}
                <Typography variant="h6">{detailDialog.label} Risk Details</Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#F9FAFB' }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: getRiskColor(detailDialog.currentRisk) }}>
                      {detailDialog.currentRisk.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Current Risk</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#F9FAFB' }}>
                    <Typography variant="h4" fontWeight={700}>
                      {detailDialog.affectedLocations}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Locations</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#FEF3C7' }}>
                    <Typography variant="h5" fontWeight={700} color="warning.dark">
                      {formatCurrency(detailDialog.affectedValue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">At Risk</Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Risk Trend</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTrendIcon(detailDialog.trend)}
                  <Typography variant="body2">
                    {detailDialog.trend === 'increasing' ? 'Risk expected to increase' :
                     detailDialog.trend === 'decreasing' ? 'Risk expected to decrease' :
                     'Risk expected to remain stable'}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Seasonal Status</Typography>
                <Chip 
                  label={detailDialog.seasonalPeak ? 'Peak Season' : 'Off Season'}
                  color={detailDialog.seasonalPeak ? 'warning' : 'default'}
                  size="small"
                />
              </Box>
              
              {detailDialog.warnings.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Warnings</Typography>
                  <List dense>
                    {detailDialog.warnings.map((warning, i) => (
                      <ListItem key={i}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <WarningIcon color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={warning} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialog(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}